// src/pages/news/NewsLanding.jsx
import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import newsData from "../../data/newsData";

const containerStagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20, filter: "blur(4px)" },
  show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.45, ease: "easeOut" } },
};

const cardHover = {
  hover: { y: -6, scale: 1.01, transition: { duration: 0.2 } },
  tap: { scale: 0.99 },
};

export default function NewsLanding() {
  const [visibleCount, setVisibleCount] = useState(6);
  const [selectedTag, setSelectedTag] = useState("All");
  const [glow, setGlow] = useState(false);
  const filterRef = useRef(null);
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();

  // All available tags
  const allTags = useMemo(
    () => ["All", ...new Set(newsData.flatMap((post) => post.tags || []))],
    []
  );

  // Filtered data
  const filteredPosts = useMemo(() => {
    if (selectedTag === "All") return newsData;
    return newsData.filter((p) => p.tags?.includes(selectedTag));
  }, [selectedTag]);

  const featuredPosts = filteredPosts.filter((p) => p.featured);
  const normalPosts = filteredPosts.filter((p) => !p.featured);

  const handleLoadMore = useCallback(() => setVisibleCount((v) => v + 6), []);

  const scrollToFilter = useCallback(() => {
    if (filterRef.current) {
      const top = filterRef.current.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, []);

  const handleTagClick = useCallback(
    (tag) => {
      setSelectedTag(tag);
      setVisibleCount(6);
      setGlow(true);
      setTimeout(() => setGlow(false), 700);
      scrollToFilter();
      // Update hash so deep links reflect current filter
      const encoded = tag === "All" ? "" : `#${encodeURIComponent(tag)}`;
      if (encoded !== location.hash) {
        window.history.replaceState(null, "", `/news${encoded}`);
      }
    },
    [location.hash, scrollToFilter]
  );

  // Read initial tag from URL hash and listen for hash changes
  useEffect(() => {
    const applyHash = () => {
      const raw = (location.hash || "").replace(/^#/, "");
      const tag = decodeURIComponent(raw);
      if (tag && allTags.includes(tag)) {
        setSelectedTag(tag);
        setVisibleCount(6);
      } else if (!tag) {
        setSelectedTag("All");
        setVisibleCount(6);
      }
    };
    applyHash();

    const onHashChange = () => applyHash();
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [location.hash, allTags]);

  return (
    <div className="relative">
      {/* soft background */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-blue-50 via-white to-white" />
      <div className="pointer-events-none absolute -top-24 -left-24 h-64 w-64 rounded-full bg-blue-200/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-cyan-200/40 blur-3xl" />

      {/* Hero */}
      <motion.section
        initial={{ opacity: 0, y: 18, scale: prefersReducedMotion ? 1 : 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.55 } }}
        className="mx-auto max-w-7xl px-4 pt-24 pb-6 text-center"
      >
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-blue-800">
          News & Updates
        </h1>
        <p className="mt-2 text-gray-600">
          Latest insights, visa alerts, and study tips—curated for international students.
        </p>
      </motion.section>

      {/* Floating Filter Bar */}
      <div className="mx-auto max-w-7xl px-4">
        <div
          ref={filterRef}
          className={`sticky top-16 z-40 rounded-2xl bg-white/80 backdrop-blur ring-1 ring-gray-200 shadow-sm px-3 py-2 md:px-4 md:py-3 mb-8 transition
            ${glow ? "ring-4 ring-yellow-300/70 shadow-lg" : ""}`}
        >
          <motion.div
            variants={containerStagger}
            initial="hidden"
            animate="show"
            className="relative flex flex-wrap items-center justify-center gap-2 md:gap-3"
          >
            {allTags.map((tag) => {
              const active = selectedTag === tag;
              return (
                <motion.button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  whileTap={{ scale: 0.95 }}
                  className={`relative px-4 py-1.5 rounded-full text-sm font-medium transition
                    ${active ? "text-white" : "text-gray-700 hover:text-gray-900"}`}
                  style={{
                    background: active
                      ? "linear-gradient(90deg, #2563eb 0%, #4f46e5 50%, #06b6d4 100%)"
                      : "transparent",
                  }}
                >
                  {tag}
                  {active && (
                    <span className="absolute -bottom-1 left-1/2 h-1 w-6 -translate-x-1/2 rounded-full bg-blue-500/80" />
                  )}
                </motion.button>
              );
            })}
          </motion.div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 pb-14">
        {/* Featured */}
        {featuredPosts.length > 0 && (
          <section className="mb-10">
            <motion.h2
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className="text-2xl font-bold mb-5 text-blue-700"
            >
              Featured News
            </motion.h2>
            <motion.div
              variants={containerStagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              className="grid gap-6 md:grid-cols-3"
            >
              {featuredPosts.map((post) => (
                <motion.article
                  key={post.id}
                  variants={fadeUp}
                  whileHover="hover"
                  whileTap="tap"
                  className="group relative overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200 shadow-sm"
                >
                  <Link to={`/news/${post.slug}`}>
                    <span className="absolute left-3 top-3 z-10 rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white shadow">
                      Featured
                    </span>
                    <div className="relative h-48 w-full overflow-hidden">
                      <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200" />
                      <img
                        src={post.image}
                        alt={post.title}
                        onLoad={(e) => (e.currentTarget.previousSibling.style.display = "none")}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-gray-500">{post.date}</span>
                      <h3 className="mt-1 text-lg font-semibold text-gray-900">{post.title}</h3>
                      <p className="mt-2 text-sm text-gray-600">{post.excerpt}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {post.tags?.map((tag, i) => (
                          <button
                            key={i}
                            onClick={(e) => {
                              e.preventDefault();
                              handleTagClick(tag);
                            }}
                            className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700 ring-1 ring-blue-100 hover:bg-blue-100"
                          >
                            {tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  </Link>
                  <motion.div variants={cardHover} />
                </motion.article>
              ))}
            </motion.div>
          </section>
        )}

        {/* Latest */}
        <section>
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="text-2xl font-bold mb-5 text-blue-700"
          >
            Latest News
          </motion.h2>

          <motion.div
            variants={containerStagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid gap-6 md:grid-cols-3"
          >
            {normalPosts.slice(0, visibleCount).map((post) => (
              <motion.article
                key={post.id}
                variants={fadeUp}
                whileHover="hover"
                whileTap="tap"
                className="group relative overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200 shadow-sm"
              >
                <Link to={`/news/${post.slug}`}>
                  <div className="relative h-48 w-full overflow-hidden">
                    <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200" />
                    <img
                      src={post.image}
                      alt={post.title}
                      onLoad={(e) => (e.currentTarget.previousSibling.style.display = "none")}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-xs text-gray-500">{post.date}</span>
                    <h3 className="mt-1 text-lg font-semibold text-gray-900">{post.title}</h3>
                    <p className="mt-2 text-sm text-gray-600">{post.excerpt}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {post.tags?.map((tag, i) => (
                        <button
                          key={i}
                          onClick={(e) => {
                            e.preventDefault();
                            handleTagClick(tag);
                          }}
                          className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 ring-1 ring-gray-200 hover:bg-gray-200"
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                </Link>
                <motion.div variants={cardHover} />
              </motion.article>
            ))}
          </motion.div>

          {/* Load more */}
          {visibleCount < normalPosts.length && (
            <div className="mt-8 text-center">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleLoadMore}
                className="relative inline-flex items-center justify-center overflow-hidden rounded-xl bg-blue-600 px-6 py-2.5 font-semibold text-white shadow
                           hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-200"
              >
                <span className="absolute inset-0 -z-10 translate-x-[-100%] bg-white/20 blur-sm transition group-hover:translate-x-[100%]" />
                Load More
              </motion.button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
